/**
 * The board: one socket, one reducer, one set of actions.
 *
 * ## The load order, and why it is snapshot-then-connect
 *
 * `GET /state` is fetched first, and the socket is opened second with
 * `since = <snapshot sequence>`. That order closes a race that the reverse order
 * leaves open:
 *
 * - Connect-then-fetch: events arriving between the socket opening and the
 *   snapshot landing are applied to an empty board and then overwritten by the
 *   snapshot, which is older. The board silently loses those events.
 * - Fetch-then-connect: any event that lands between the two is *after* the
 *   snapshot's sequence, so the handshake replays it. Nothing is lost, and
 *   duplicates are impossible because the client's watermark is the snapshot's
 *   sequence.
 *
 * The rule is the same one the server uses when it assembles a snapshot:
 * duplicates are avoidable, gaps are not, so order the reads to make gaps
 * impossible.
 *
 * ## Two transports, one mutation path
 *
 * A socket spends part of its life reconnecting. A board that refuses every edit
 * for those two seconds is a board that feels broken, so every mutation has both
 * a socket frame and an HTTP request, and this module picks whichever is
 * available. That is only safe because the REST routes are thin proxies over the
 * Durable Object's `POST /mutate`: same validation, same version check, same
 * event, same broadcast. Two implementations of "move a card" would diverge, and
 * the divergence would show up as a board that behaves differently depending on
 * which transport happened to be up.
 *
 * Not every operation has a socket form. Deleting a card, editing a comment,
 * deleting a comment and every attachment operation are HTTP-only - they are
 * infrequent, several are destructive, and a socket frame for each would be
 * protocol surface with nothing behind it. The choice is deliberate and visible
 * in the code below.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Card, CardComment, CardId, ColumnId, PublicUser, ServerMessage, User } from '@slate/shared';
import type { ApiClient } from '../api/client.js';
import { ApiError } from '../api/client.js';
import { isDelta, isSnapshot, type BoardStateResponse } from '../api/types.js';
import { BoardSocket } from '../realtime/connection.js';
import { newOpId } from '../lib/ids.js';
import { materialise, type BoardView } from './materialise.js';
import type { BoardAction } from './board-reducer.js';
import {
  acknowledgePending,
  acknowledgedOpId,
  boardReducer,
  dropPending,
  makeNotice,
  markSent,
  mergePending,
} from './board-reducer.js';
import { initialBoardState, type BoardState, type CardPatch, type DragPreview, type PendingOp } from './types.js';

export interface BoardActions {
  moveCard(cardId: CardId, columnId: ColumnId, index: number): void;
  createCard(columnId: ColumnId, title: string, options?: { description?: string | null }): void;
  updateCard(cardId: CardId, patch: CardPatch): void;
  archiveCard(cardId: CardId, archived: boolean): void;
  deleteCard(cardId: CardId): Promise<void>;
  createColumn(name: string): void;
  reorderColumn(columnId: ColumnId, index: number): void;
  /** HTTP only: the socket protocol has no `column.rename` frame, and a rename is
   * a single infrequent text edit that gains nothing from a persistent
   * connection. */
  renameColumn(columnId: ColumnId, name: string): Promise<void>;
  /** Archive or restore the board itself. */
  archiveBoard(archived: boolean): Promise<void>;
  /** Hard-delete the board. Resolves `false` when the server refused, in which
   * case a notice has already been raised. */
  deleteBoard(): Promise<boolean>;
  /** Delete a column. Resolves with the number of cards the server refused to
   * discard, so the caller can ask what to do with them - see the route docblock
   * in `apps/api/src/routes/columns.ts` for why the server refuses to guess. */
  deleteColumn(
    columnId: ColumnId,
    strategy?: { kind: 'archive' } | { kind: 'move'; targetColumnId: string },
  ): Promise<{ ok: true } | { ok: false; cardCount: number; message: string }>;
  createComment(cardId: CardId, body: string): void;
  updateComment(cardId: CardId, commentId: string, body: string): Promise<void>;
  deleteComment(cardId: CardId, commentId: string): Promise<void>;
  uploadAttachment(cardId: CardId, file: File): Promise<void>;
  deleteAttachment(cardId: CardId, attachmentId: string): Promise<void>;
  /** Fetch everything the drawer needs in one request. */
  openCardDetail(cardId: CardId): Promise<void>;
  loadActivity(options?: { reset?: boolean }): Promise<void>;
  sendCursor(x: number, y: number): void;
  flushCursor(): void;
  setTyping(cardId: CardId | null, isTyping: boolean): void;
  dismissNotice(id: string): void;
}

export interface BoardContextValue {
  state: BoardState;
  view: BoardView;
  actions: BoardActions;
  /** The socket, exposed so a component can report whether it is open. */
  socket: BoardSocket | null;
}

const BoardContext = createContext<BoardContextValue | null>(null);

/** A separate context for the drag preview setter.
 *
 * Deliberately split from `BoardContext`. The preview changes on every pointer
 * crossing during a drag; if it lived in the main value, every consumer - the
 * activity feed, the drawer, the header - would re-render at pointer rate. Here
 * only the components that opt in re-render, which in practice is the board
 * surface and the columns. */
const PreviewSetter = createContext<((preview: DragPreview | null) => void) | null>(null);

/** How long the activity feed waits after a live frame before refetching page
 * one. The feed is a read model with its own pagination, and rebuilding it from
 * socket frames would mean reimplementing the server's event projection on the
 * client - two projections that would drift. Refetching costs one small request
 * per burst of activity. */
const ACTIVITY_REFRESH_DEBOUNCE_MS = 1_200;

/** A fallback for an acknowledgement that never arrives. The server always sends
 * one, but a socket that closes between the send and the echo would otherwise
 * leave the update loop waiting forever. */
const ACK_TIMEOUT_MS = 5_000;

export interface BoardProviderProps {
  boardId: string;
  client: ApiClient;
  /** Pushed into the socket on every connect rather than captured, because the
   * access token rotates every fifteen minutes and a captured value would be the
   * one from the first connection. */
  getAccessToken(): string | null;
  /** Used to build an optimistic comment's author. */
  currentUser: User;
  children: ReactNode;
}

export function BoardProvider({
  boardId,
  client,
  getAccessToken,
  currentUser,
  children,
}: BoardProviderProps): ReactNode {
  const [state, rawDispatch] = useReducer(boardReducer, undefined, initialBoardState);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const [socket, setSocket] = useState<BoardSocket | null>(null);

  /** Extracted so the socket effect's dependency is the flag itself rather than
   * the whole state object, which changes on every frame. */
  const { boardUnavailable } = state;

  const socketRef = useRef<BoardSocket | null>(null);
  const stateRef = useRef(state);
  const pendingRef = useRef<PendingOp[]>([]);
  const ackWaiters = useRef(new Map<string, () => void>());
  const updateLoops = useRef(new Set<string>());
  const getTokenRef = useRef(getAccessToken);

  stateRef.current = state;
  getTokenRef.current = getAccessToken;

  /**
   * Dispatch, keeping the pending mirror in step synchronously.
   *
   * The mirror exists because `commitCardUpdate` runs an async send loop that has
   * to read the *current* pending operation between awaits, and a React ref
   * updated from an effect is not guaranteed to have caught up by the time a
   * microtask continuation runs. The mirror is updated here, at the call, using
   * the same pure helpers the reducer uses - so the two cannot disagree.
   */
  const dispatch = useCallback((action: BoardAction) => {
    switch (action.type) {
      case 'optimistic':
        pendingRef.current = mergePending(pendingRef.current, action.op);
        break;
      case 'sent':
        pendingRef.current = markSent(pendingRef.current, action.opId, action.revision);
        break;
      case 'retire':
        pendingRef.current = acknowledgePending(pendingRef.current, action.opId);
        break;
      case 'settle':
        pendingRef.current = dropPending(pendingRef.current, action.opId);
        break;
      case 'rejected':
        if (action.opId !== null) pendingRef.current = dropPending(pendingRef.current, action.opId);
        break;
      default:
        break;
    }
    rawDispatch(action);
  }, []);

  const notify = useCallback(
    (tone: 'info' | 'error' | 'success', message: string) => {
      dispatch({ type: 'notice', notice: makeNotice(tone, message) });
    },
    [dispatch],
  );

  const fail = useCallback(
    (error: unknown, opId: string | null) => {
      if (error instanceof ApiError) {
        // A `version_conflict` carries the authoritative row. Dispatching it as a
        // rejection lets the reducer reconcile in the same tick, which is what
        // makes rollback feel instant rather than like a failed request.
        dispatch({ type: 'rejected', opId, message: error.detail, current: error.current });
        return;
      }
      dispatch({
        type: 'rejected',
        opId,
        message: error instanceof Error ? error.message : 'The change could not be applied.',
        current: null,
      });
    },
    [dispatch],
  );

  // -------------------------------------------------------------------------
  // Initial load
  // -------------------------------------------------------------------------

  const applyStateResponse = useCallback(
    (response: BoardStateResponse) => {
      if (isSnapshot(response)) {
        dispatch({
          type: 'snapshot',
          board: response.board,
          columns: response.columns,
          cards: response.cards,
          labels: response.labels,
          members: response.members,
          sequence: response.sequence,
        });
        return;
      }
      if (isDelta(response)) {
        dispatch({ type: 'events', events: response.events, sequence: response.sequence });
        return;
      }
      // `resyncRequired`: the gap is larger than the replay window. The server
      // makes this call rather than the client, because only the server knows how
      // many events the gap contains.
      dispatch({ type: 'resync-needed', needed: true });
    },
    [dispatch],
  );

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'reset', boardId });
    dispatch({ type: 'loading' });
    pendingRef.current = [];

    void (async () => {
      try {
        const response = await client.boardState(boardId);
        if (cancelled) return;
        applyStateResponse(response);
      } catch (error) {
        if (cancelled) return;
        // A 401, 403 or 404 is the server saying no, not failing. Anything else -
        // a timeout, a 5xx, a dropped connection - might resolve on its own, so
        // the socket is still opened and the board can recover from a live frame
        // or a reconnect. The distinction exists because a rejected WebSocket
        // upgrade is reported to script as `1006` with no HTTP status, which the
        // reconnect logic treats as always-retryable: without this, a board the
        // user cannot see would be retried forever.
        const terminal =
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403 || error.status === 404);

        dispatch({
          type: 'failed',
          message: error instanceof ApiError ? error.detail : 'The board could not be loaded.',
          terminal,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [boardId, client, applyStateResponse, dispatch]);

  // -------------------------------------------------------------------------
  // Socket
  // -------------------------------------------------------------------------

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      // Acknowledge before applying, so the pending mirror is correct when the
      // reducer runs. The reducer retires the operation as part of applying the
      // frame as well; the duplicate is idempotent and keeps the mirror honest.
      const opId = acknowledgedOpId(message);
      if (opId !== null) {
        dispatch({ type: 'retire', opId });
        const waiter = ackWaiters.current.get(opId);
        if (waiter !== undefined) {
          ackWaiters.current.delete(opId);
          waiter();
        }
      }
      dispatch({ type: 'server-message', message });
    },
    [dispatch],
  );

  useEffect(() => {
    // See `boardUnavailable`. Opening a socket for a board the server has refused
    // would be answered with a rejected upgrade, which the browser reports as
    // `1006` - indistinguishable from a network failure - and the client would
    // retry it forever. The dependency is on the boolean, not on `state`, so the
    // effect re-runs only when availability actually changes.
    if (boardUnavailable) return;

    const instance = new BoardSocket({
      boardId,
      getToken: () => getTokenRef.current(),
      getSince: () => stateRef.current.sequence,
      getResumeClientId: () => stateRef.current.clientId,
      onMessage: handleMessage,
      onStatus: (status) => dispatch({ type: 'connection', status }),
      onFatal: (reason) => {
        dispatch({ type: 'connection', status: 'offline' });
        notify('error', reason);
      },
      onLatency: (ms) => dispatch({ type: 'latency', ms }),
    });

    socketRef.current = instance;
    setSocket(instance);
    instance.connect();

    const onVisibility = () => instance.setCursorsEnabled(document.visibilityState === 'visible');
    const onOnline = () => {
      // The browser knows the network came back before the socket's own backoff
      // timer would have fired. Reconnecting immediately turns a thirty-second
      // worst case into a sub-second one.
      if (instance.currentStatus === 'reconnecting' || instance.currentStatus === 'offline') instance.connect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      instance.close();
      socketRef.current = null;
      setSocket(null);
    };
  }, [boardId, boardUnavailable, dispatch, handleMessage, notify]);

  // -------------------------------------------------------------------------
  // Resync
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!state.needsResync) return;
    let cancelled = false;
    dispatch({ type: 'resync-needed', needed: false });

    void (async () => {
      try {
        // Re-read from the local watermark. The server decides whether that is a
        // small delta or a full snapshot; the client does not have to guess, and
        // guessing wrong is expensive in one direction and incorrect in the
        // other.
        const response = await client.boardState(boardId, stateRef.current.sequence);
        if (cancelled) return;
        applyStateResponse(response);
      } catch {
        if (cancelled) return;
        // Left flagged so the next trigger retries. The board stays renderable
        // from stale state in the meantime, and the connection badge shows that
        // something is wrong.
        dispatch({ type: 'resync-needed', needed: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.needsResync, boardId, client, applyStateResponse, dispatch]);

  // -------------------------------------------------------------------------
  // Activity feed
  // -------------------------------------------------------------------------

  const loadActivity = useCallback(
    async (options: { reset?: boolean } = {}) => {
      try {
        const cursor = options.reset === true ? undefined : (stateRef.current.activityCursor ?? undefined);
        const page = await client.activity(boardId, cursor, 30);
        dispatch({
          type: 'activity-page',
          events: page.events,
          nextCursor: page.nextCursor,
          append: options.reset !== true,
        });
      } catch {
        // The feed is secondary. A failure to load it must not disturb the board,
        // and the user can retry by scrolling.
      }
    },
    [boardId, client, dispatch],
  );

  useEffect(() => {
    void loadActivity({ reset: true });
  }, [loadActivity]);

  useEffect(() => {
    if (!state.activityStale) return;
    const timer = setTimeout(() => {
      void loadActivity({ reset: true });
    }, ACTIVITY_REFRESH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state.activityStale, loadActivity]);

  // -------------------------------------------------------------------------
  // Transport helpers
  // -------------------------------------------------------------------------

  const waitForAck = useCallback((opId: string): Promise<void> => {
    return new Promise((resolve) => {
      ackWaiters.current.set(opId, resolve);
      setTimeout(() => {
        const waiter = ackWaiters.current.get(opId);
        if (waiter === resolve) {
          ackWaiters.current.delete(opId);
          resolve();
        }
      }, ACK_TIMEOUT_MS);
    });
  }, []);

  /** Send a frame, falling back to `null` when the socket is down so the caller
   * knows to take the HTTP path. */
  const sendFrame = useCallback((message: Parameters<BoardSocket['send']>[0]): boolean => {
    return socketRef.current?.send(message) ?? false;
  }, []);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const actions = useMemo<BoardActions>(() => {
    const versionOf = (cardId: CardId): number | null => {
      const card = stateRef.current.cards[cardId];
      return card === undefined ? null : card.version;
    };

    /** The current user as the public projection the wire uses. */
    const actor: PublicUser = {
      id: currentUser.id,
      name: currentUser.name,
      colour: currentUser.colour,
      avatarUrl: currentUser.avatarUrl,
    };

    async function openCardDetail(cardId: CardId): Promise<void> {
      try {
        const detail = await client.cardDetail(boardId, cardId);
        dispatch({
          type: 'card-detail',
          cardId,
          card: detail.card,
          comments: detail.comments,
          attachments: detail.attachments,
          history: detail.history,
        });
      } catch (error) {
        fail(error, null);
      }
    }

    return {
      moveCard(cardId, columnId, index) {
        const version = versionOf(cardId);
        if (version === null) return;

        const opId = newOpId();
        dispatch({ type: 'optimistic', op: { opId, kind: 'card.move', at: Date.now(), cardId, columnId, index } });

        if (sendFrame({ t: 'card.move', d: { opId, cardId, columnId, position: index, expectedVersion: version } })) {
          return;
        }

        void client
          .moveCard(boardId, cardId, { columnId, position: index, expectedVersion: version, opId })
          .then((response) => dispatch({ type: 'settle', opId, card: response.card }))
          .catch((error: unknown) => fail(error, opId));
      },

      createCard(columnId, title, options) {
        const board = stateRef.current.board;
        if (board === null) return;

        // The id is minted here so the optimistic card renders with its final
        // identity and the acknowledgement is an upsert rather than a rename that
        // would remount the node mid-drag. See `lib/ids.ts`.
        const cardId = crypto.randomUUID();
        const now = new Date().toISOString();

        const optimistic: Card = {
          id: cardId,
          boardId: board.id,
          columnId,
          title,
          description: options?.description ?? null,
          assigneeId: null,
          dueDate: null,
          priority: 'none',
          // The overlay does not use this - `materialise` appends a created card
          // to the end of its column - but the field is required by the type and
          // the server's real value arrives with the acknowledgement.
          position: Number.MAX_SAFE_INTEGER,
          // Version 1 is what the server reports for a new card, so the drawer's
          // first conditional write does not start with a version that has never
          // existed.
          version: 1,
          archivedAt: null,
          createdBy: currentUser.id,
          createdAt: now,
          updatedAt: now,
          labels: [],
        };

        const opId = newOpId();
        dispatch({ type: 'optimistic', op: { opId, kind: 'card.create', at: Date.now(), card: optimistic } });

        const sent = sendFrame({
          t: 'card.create',
          d: {
            opId,
            id: cardId,
            columnId,
            title,
            description: options?.description ?? null,
            priority: 'none',
            assigneeId: null,
            dueDate: null,
          },
        });
        if (sent) return;

        void client
          .createCard(boardId, { id: cardId, columnId, title, description: options?.description ?? null })
          .then((response) => dispatch({ type: 'settle', opId, card: response.card }))
          .catch((error: unknown) => fail(error, opId));
      },

      /**
       * The coalescing update loop.
       *
       * Edits to the same card merge into one pending operation. A loop per
       * operation sends, waits for the acknowledgement, and sends again only if
       * the patch grew while the frame was in flight. The effect is one frame per
       * round trip regardless of how fast the user types, and no two frames ever
       * carry the same `expectedVersion` - which is the failure the naive version
       * has, and it fails by losing the user's edit.
       */
      updateCard(cardId, patch) {
        const existing = pendingRef.current.find(
          (op): op is Extract<PendingOp, { kind: 'card.update' }> => op.kind === 'card.update' && op.cardId === cardId,
        );

        const opId = existing?.opId ?? newOpId();
        dispatch({
          type: 'optimistic',
          op: {
            opId,
            kind: 'card.update',
            at: Date.now(),
            cardId,
            patch,
            revision: (existing?.revision ?? 0) + 1,
            sentRevision: existing?.sentRevision ?? 0,
          },
        });

        // A loop is already running for this operation; it will notice the new
        // revision when its acknowledgement arrives.
        if (updateLoops.current.has(opId)) return;
        updateLoops.current.add(opId);

        void (async () => {
          try {
            for (;;) {
              const current = pendingRef.current.find((entry) => entry.opId === opId);
              if (current === undefined || current.kind !== 'card.update') return;

              const base = stateRef.current.cards[cardId];
              if (base === undefined) return;

              const revision = current.revision;
              dispatch({ type: 'sent', opId, revision });

              const ack = waitForAck(opId);

              if (!sendFrame({ t: 'card.update', d: { opId, cardId, patch: current.patch, expectedVersion: base.version } })) {
                try {
                  const response = await client.updateCard(boardId, cardId, current.patch, base.version);
                  dispatch({ type: 'settle', opId, card: response.card });
                } catch (error) {
                  fail(error, opId);
                }
                return;
              }

              await ack;

              // Re-read after the await. If the patch grew, go round again - the
              // base version has advanced, so the next frame is not stale.
              const after = pendingRef.current.find((entry) => entry.opId === opId);
              if (after === undefined || after.kind !== 'card.update') return;
              if (after.revision === revision) return;
            }
          } finally {
            updateLoops.current.delete(opId);
          }
        })();
      },

      archiveCard(cardId, archived) {
        const version = versionOf(cardId);
        if (version === null) return;

        const opId = newOpId();
        // Only an archive produces an optimistic overlay. Un-archiving would have
        // to place the card back at a position the client does not know, and the
        // card is not in the view to be un-archived from - the archived list is a
        // separate, non-optimistic surface.
        if (archived) {
          dispatch({ type: 'optimistic', op: { opId, kind: 'card.archive', at: Date.now(), cardId } });
        }

        if (sendFrame({ t: 'card.archive', d: { opId, cardId, archived, expectedVersion: version } })) return;

        void client
          .archiveCard(boardId, cardId, archived, version)
          .then((response) => dispatch({ type: 'settle', opId, card: response.card }))
          .catch((error: unknown) => fail(error, opId));
      },

      async deleteCard(cardId) {
        const version = versionOf(cardId);
        if (version === null) return;

        const opId = newOpId();
        dispatch({ type: 'optimistic', op: { opId, kind: 'card.delete', at: Date.now(), cardId } });

        // HTTP only: there is no `card.delete` frame in the protocol. Deleting is
        // rare, destructive and already behind a confirmation, so a socket frame
        // for it would be surface with nothing behind it.
        try {
          await client.deleteCard(boardId, cardId, version);
          dispatch({ type: 'settle', opId });
        } catch (error) {
          fail(error, opId);
        }
      },

      createColumn(name) {
        const board = stateRef.current.board;
        if (board === null) return;

        const opId = newOpId();
        const now = new Date().toISOString();
        dispatch({
          type: 'optimistic',
          op: {
            opId,
            kind: 'column.create',
            at: Date.now(),
            // The id is local and will not match the server's. `materialise`
            // appends rather than sorts, and the acknowledgement replaces this
            // placeholder outright, so the mismatch lasts one round trip and the
            // user sees the column appear immediately.
            column: { id: crypto.randomUUID(), boardId: board.id, name, position: 0, createdAt: now, updatedAt: now },
          },
        });

        if (sendFrame({ t: 'column.create', d: { opId, name } })) return;

        void client
          .createColumn(boardId, name)
          .then((response) => dispatch({ type: 'settle', opId, column: response.column }))
          .catch((error: unknown) => fail(error, opId));
      },

      reorderColumn(columnId, index) {
        const opId = newOpId();
        dispatch({ type: 'optimistic', op: { opId, kind: 'column.reorder', at: Date.now(), columnId, index } });

        if (sendFrame({ t: 'column.reorder', d: { opId, columnId, position: index } })) return;

        void client
          .updateColumn(boardId, columnId, { position: index })
          .then((response) => dispatch({ type: 'settle', opId, column: response.column }))
          .catch((error: unknown) => fail(error, opId));
      },

      /**
       * Rename a column.
       *
       * Not optimistic, and the only mutation here that is not. A rename is
       * committed on blur or Enter, at which point the user has already stopped
       * looking at the field; an overlay that raced a peer's rename would show one
       * name for a few milliseconds and then another. The round trip is short and
       * the result is what the server broadcast to everyone, so showing the
       * server's answer directly is both simpler and more correct.
       */
      async renameColumn(columnId, name) {
        try {
          const response = await client.updateColumn(boardId, columnId, { name });
          dispatch({ type: 'column-upsert', column: response.column });
        } catch (error) {
          fail(error, null);
        }
      },

      async archiveBoard(archived) {
        try {
          await client.archiveBoard(boardId, archived);
          // The board's own `board.updated` event travels as the generic `event`
          // frame, and the reducer applies it to `state.board`. Nothing to do
          // here beyond surfacing a failure.
        } catch (error) {
          fail(error, null);
        }
      },

      async deleteBoard() {
        try {
          await client.deleteBoard(boardId);
          return true;
        } catch (error) {
          fail(error, null);
          return false;
        }
      },

      /**
       * Delete a column.
       *
       * Not optimistic, unlike every other mutation here. A column deletion can
       * take one of three forms - it may be refused because the column still
       * holds cards - and guessing which before the server answers would mean
       * either hiding a column that turns out to still exist or showing a
       * "what about these cards?" dialog for a deletion that already happened.
       * The round trip is a few milliseconds and the operation is rare.
       */
      async deleteColumn(columnId, strategy) {
        try {
          await client.deleteColumn(boardId, columnId, strategy);
          return { ok: true as const };
        } catch (error) {
          if (error instanceof ApiError && error.code === 'conflict') {
            const raw = error.extensions['cardCount'];
            return {
              ok: false as const,
              cardCount: typeof raw === 'number' ? raw : 0,
              message: error.detail,
            };
          }
          fail(error, null);
          return { ok: false as const, cardCount: 0, message: 'The column could not be deleted.' };
        }
      },

      createComment(cardId, body) {
        const board = stateRef.current.board;
        if (board === null) return;

        const opId = newOpId();
        const optimistic: CardComment = {
          id: crypto.randomUUID(),
          cardId,
          boardId: board.id,
          author: actor,
          body,
          editedAt: null,
          deletedAt: null,
          createdAt: new Date().toISOString(),
        };
        dispatch({ type: 'optimistic', op: { opId, kind: 'comment.create', at: Date.now(), cardId, comment: optimistic } });

        if (sendFrame({ t: 'comment.create', d: { opId, cardId, body } })) return;

        void client
          .createComment(boardId, cardId, body)
          .then((response) => dispatch({ type: 'settle', opId, comment: response.comment }))
          .catch((error: unknown) => fail(error, opId));
      },

      async updateComment(cardId, commentId, body) {
        try {
          const response = await client.updateComment(boardId, cardId, commentId, body);
          const updated = response.comment;
          if (updated === null) return;
          const existing = stateRef.current.comments[cardId] ?? [];
          dispatch({
            type: 'comments',
            cardId,
            comments: existing.map((entry) => (entry.id === commentId ? updated : entry)),
          });
        } catch (error) {
          fail(error, null);
        }
      },

      async deleteComment(cardId, commentId) {
        try {
          await client.deleteComment(boardId, cardId, commentId);
          // The server soft-deletes and broadcasts a tombstone, but the broadcast
          // is the generic `event` frame and the reducer applies it only to a
          // thread that is already loaded. Reloading the detail is the reliable
          // path, and this is a rare operation.
          await openCardDetail(cardId);
        } catch (error) {
          fail(error, null);
        }
      },

      async uploadAttachment(cardId, file) {
        try {
          const response = await client.uploadAttachment(boardId, cardId, file);
          if (response.attachment !== null) {
            const existing = stateRef.current.attachments[cardId] ?? [];
            dispatch({ type: 'attachments', cardId, attachments: [...existing, response.attachment] });
          }
          notify('success', `${file.name} uploaded.`);
        } catch (error) {
          fail(error, null);
        }
      },

      async deleteAttachment(cardId, attachmentId) {
        try {
          await client.deleteAttachment(boardId, cardId, attachmentId);
          const existing = stateRef.current.attachments[cardId] ?? [];
          dispatch({ type: 'attachments', cardId, attachments: existing.filter((item) => item.id !== attachmentId) });
        } catch (error) {
          fail(error, null);
        }
      },

      openCardDetail,

      loadActivity,

      sendCursor(x, y) {
        socketRef.current?.sendCursor({ x, y });
      },

      flushCursor() {
        socketRef.current?.flushCursor();
      },

      setTyping(cardId, isTyping) {
        sendFrame({ t: 'typing', d: { cardId, isTyping } });
      },

      dismissNotice(id) {
        dispatch({ type: 'dismiss-notice', id });
      },
    };
  }, [boardId, client, currentUser, dispatch, fail, loadActivity, notify, sendFrame, waitForAck]);

  const view = useMemo(() => materialise(state, preview), [state, preview]);

  const value = useMemo<BoardContextValue>(() => ({ state, view, actions, socket }), [state, view, actions, socket]);

  return (
    <BoardContext.Provider value={value}>
      <PreviewSetter.Provider value={setPreview}>{children}</PreviewSetter.Provider>
    </BoardContext.Provider>
  );
}

export function usePreviewSetter(): (preview: DragPreview | null) => void {
  const setter = useContext(PreviewSetter);
  if (setter === null) throw new Error('usePreviewSetter must be used inside <BoardProvider>.');
  return setter;
}

export function useBoard(): BoardContextValue {
  const value = useContext(BoardContext);
  if (value === null) throw new Error('useBoard must be used inside <BoardProvider>.');
  return value;
}

export function useBoardActions(): BoardActions {
  return useBoard().actions;
}
